import { useEffect, useState } from "react";
import { useCohortRules } from "@/api/cohort-rules";
import { RuleBuilderDrawer } from "./components/rule-builder-drawer";
import { RulesListDrawer } from "./components/rules-list-drawer";
import { RulesListEmptyDrawer } from "./components/rules-list-empty-drawer";
import { RulesListLoadingDrawer } from "./components/rules-list-loading-drawer";

type View = { kind: "list" } | { kind: "builder"; ruleId?: string };

interface CohortRulesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens straight into the builder for this rule, rather than on the list. */
  initialRuleId?: string;
  /** Notifies a caller holding its own cohort draft that a rule/exemption mutation may have changed assignment counts. */
  onCohortsChanged?: () => void;
}

export function CohortRulesDrawer({
  open,
  onOpenChange,
  initialRuleId,
  onCohortsChanged = () => {},
}: CohortRulesDrawerProps) {
  const [view, setView] = useState<View>({ kind: "list" });
  const { data, isLoading } = useCohortRules();

  // Reopening the panel lands on the rule it was opened for, or on the list —
  // never back on an abandoned draft.
  useEffect(() => {
    setView(open && initialRuleId ? { kind: "builder", ruleId: initialRuleId } : { kind: "list" });
  }, [open, initialRuleId]);

  const openBuilder = (ruleId?: string) => setView({ kind: "builder", ruleId });
  const backToList = () => setView({ kind: "list" });

  if (view.kind === "builder") {
    return (
      <RuleBuilderDrawer
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) backToList();
        }}
        ruleId={view.ruleId}
        onBack={backToList}
        onCohortsChanged={onCohortsChanged}
      />
    );
  }

  if (isLoading) return <RulesListLoadingDrawer open={open} onOpenChange={onOpenChange} />;

  if (!data?.items.length) {
    return <RulesListEmptyDrawer open={open} onOpenChange={onOpenChange} onCreate={() => openBuilder()} />;
  }

  return (
    <RulesListDrawer
      open={open}
      onOpenChange={onOpenChange}
      onEdit={openBuilder}
      onCreate={() => openBuilder()}
      onCohortsChanged={onCohortsChanged}
    />
  );
}
